import { defineMessages } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import BlockSettingsSchema from '@plone/volto/components/manage/Blocks/Block/Schema';
import multimediaSVG from '@plone/volto/icons/slider.svg';
import MultimediaView from '@redturtle/iovisit-theme/components/Blocks/Multimedia/View';
import MultimediaEdit from '@redturtle/iovisit-theme/components/Blocks/Multimedia/Edit';
import MultimediaFolderGalleryView from '@redturtle/iovisit-theme/components/Blocks/MultimediaFolderGallery/View';
import MultimediaFolderGalleryEdit from '@redturtle/iovisit-theme/components/Blocks/MultimediaFolderGallery/Edit';
import CardsVariation from '@redturtle/iovisit-theme/components/Blocks/Listing/CardsTemplate/Cards';
import SliderVariation from '@redturtle/iovisit-theme/components/Blocks/Listing/Slider/Slider';
import LinkCardVariation from '@redturtle/iovisit-theme/components/Blocks/Listing/LinkCard';
import LinkInEvidenceVariation from '@redturtle/iovisit-theme/components/Blocks/Listing/LinkInEvidence';
import MapTemplate from '@redturtle/iovisit-theme/components/Blocks/Listing/MapTemplate';
import BreakView from '@redturtle/iovisit-theme/components/Blocks/Break/View';
import BreakEdit from '@redturtle/iovisit-theme/components/Blocks/Break/Edit';
import divideHorizontalSVG from '@plone/volto/icons/divide-horizontal.svg';
import ContactsView from '@redturtle/iovisit-theme/components/Blocks/Contacts/View';
import ContactsEdit from '@redturtle/iovisit-theme/components/Blocks/Contacts/Edit';
import globeSVG from '@plone/volto/icons/globe.svg';
import zoomSVG from '@plone/volto/icons/zoom.svg';
import ViewSearchEvents from '@redturtle/iovisit-theme/components/Blocks/SearchEvents/View';
import EditSearchEvents from '@redturtle/iovisit-theme/components/Blocks/SearchEvents/Edit';
import ViewSearchStrutturaRicettiva from '@redturtle/iovisit-theme/components/Blocks/SearchStrutturaRicettiva/View';
import EditSearchStrutturaRicettiva from '@redturtle/iovisit-theme/components/Blocks/SearchStrutturaRicettiva/Edit';
import heroSVG from '@plone/volto/icons/hero.svg';
import countDownSVG from '@plone/volto/icons/chrono.svg';
import CountDownBlockView from '@redturtle/iovisit-theme/components/Blocks/CountDown/View';
import CountDownBlockEdit from '@redturtle/iovisit-theme/components/Blocks/CountDown/Edit';
import flashSVG from '@plone/volto/icons/flash.svg';
import CTABlockView from '@redturtle/iovisit-theme/components/Blocks/CTABlock/View';
import CTABlockEdit from '@redturtle/iovisit-theme/components/Blocks/CTABlock/Edit';

import {
  getDefaultSchema,
  getSliderVariationSchema,
  getCardsVariationSchema,
  getMapVariationSchema,
  removeListingVariation,
} from '@redturtle/iovisit-theme/config';
import { addCtaColorField } from './blocks-schema/utils';
import { addSchemaField } from './blocks-schema/utils';
import getLinkCardVariationSchema from './blocks-schema/listing-variations/link-card-variation-schema';

const messages = defineMessages({
  image_description_title: {
    id: 'image_description_title',
    defaultMessage: 'Didascalia immagine',
  },
});

export const IoVisitBlocks = (config) => {
  const customBlocksOrder = [{ id: 'search', title: 'Ricerca' }];

  config.blocks.groupBlocksOrder =
    config.blocks.groupBlocksOrder.concat(customBlocksOrder);
  config.blocks.blocksConfig = {
    ...config.blocks.blocksConfig,

    break: {
      id: 'break',
      title: 'Interruzione di pagina',
      icon: divideHorizontalSVG,
      group: 'text',
      view: BreakView,
      edit: BreakEdit,
      restricted: false,
      mostUsed: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 0,
    },

    // TODO: rimpiazzare con il blocco teaser,
    // perché hero non è più implementato in volto 18
    // hero: {
    //   id: 'hero',
    //   title: 'Hero',
    //   icon: heroSVG,
    //   group: 'common',
    //   view: ViewHeroImageLeftBlock,
    //   edit: EditHeroImageLeftBlock,
    //   schema: BlockSettingsSchema,
    //   blockSchema: HeroImageLeftBlockSchema,
    //   restricted: false,
    //   mostUsed: false,
    //   blockHasOwnFocusManagement: true,
    //   sidebarTab: 1,
    // },

    multimedia: {
      id: 'multimedia',
      title: 'Multi media',
      icon: multimediaSVG,
      group: 'media',
      view: MultimediaView,
      edit: MultimediaEdit,
      restricted: false,
      mostUsed: false,
      blockHasOwnFocusManagement: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    multimediaFolderGallery: {
      //mostra (in formato gallery) il contenuto della cartella Multimedia presente fra gli items del ct
      id: 'multimediaFolderGallery',
      title: 'Multimedia Folder Gallery',
      icon: multimediaSVG,
      group: 'media',
      view: MultimediaFolderGalleryView,
      edit: MultimediaFolderGalleryEdit,
      restricted: true,
      mostUsed: false,
      blockHasOwnFocusManagement: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    contacts: {
      id: 'contacts',
      title: 'Contatti con mappa',
      icon: globeSVG,
      group: 'text',
      view: ContactsView,
      edit: ContactsEdit,
      restricted: ({ properties, block, contentType }) => {
        if (contentType === 'Event' || contentType === 'PointOfInterest') {
          if (
            !!properties.blocks_layout?.items?.find(
              (uid) => properties.blocks?.[uid]?.['@type'] === block.id,
            )
          ) {
            return true;
          }
          return false;
        }
        return true;
      },
      mostUsed: false,
      blockHasOwnFocusManagement: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    count_down: {
      id: 'count_down',
      title: 'Count Down',
      icon: countDownSVG,
      group: 'common',
      view: CountDownBlockView,
      edit: CountDownBlockEdit,
      restricted: false,
      mostUsed: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    searchStrutturaRicettiva: {
      id: 'searchStrutturaRicettiva',
      title: 'Ricerca strutture ricettive',
      icon: zoomSVG,
      group: 'search',
      view: ViewSearchStrutturaRicettiva,
      edit: EditSearchStrutturaRicettiva,
      restricted: false,
      mostUsed: true,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    searchEvents: {
      id: 'searchEvents',
      title: 'Ricerca eventi',
      icon: zoomSVG,
      group: 'search',
      view: ViewSearchEvents,
      edit: EditSearchEvents,
      restricted: false,
      mostUsed: true,
      blockHasOwnFocusManagement: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    cta: {
      id: 'cta',
      title: 'Blocco CTA',
      icon: flashSVG,
      group: 'common',
      view: CTABlockView,
      edit: CTABlockEdit,
      restricted: false,
      mostUsed: false,
      security: {
        addPermission: [],
        view: [],
      },
      sidebarTab: 1,
    },

    listing: {
      ...config.blocks.blocksConfig.listing,
      showLinkMore: true,
      noResultsComponent: null,
      variations: [
        ...config.blocks.blocksConfig.listing.variations,
        {
          id: 'cards',
          isDefault: true,
          title: 'Cards',
          template: CardsVariation,
          schemaEnhancer: ({ schema, formData, intl }) => {
            let pos = getDefaultSchema(schema, formData, intl);
            getCardsVariationSchema(schema, formData, intl, pos);
            addCtaColorField(schema, intl);
            return schema;
          },
        },
        {
          id: 'slider',
          isDefault: false,
          title: 'Slider',
          template: SliderVariation,
          schemaEnhancer: ({ schema, formData, intl }) => {
            let pos = getDefaultSchema(schema, formData, intl);
            getSliderVariationSchema(schema, formData, intl, pos);
            return schema;
          },
        },
        {
          id: 'link-card',
          isDefault: false,
          title: 'Link card',
          template: LinkCardVariation,
          schemaEnhancer: ({ schema, formData, intl }) => {
            let pos = getDefaultSchema(schema, formData, intl);
            getLinkCardVariationSchema(schema, formData, intl, pos);
            return schema;
          },
        },
        {
          id: 'link-in-evidence',
          isDefault: false,
          title: 'Link in evidenza',
          template: LinkInEvidenceVariation,
          schemaEnhancer: ({ schema, formData, intl }) => {
            getDefaultSchema(schema, formData, intl);

            return schema;
          },
        },
        {
          id: 'map-template',
          isDefault: false,
          title: 'Mappa',
          template: MapTemplate,
          schemaEnhancer: ({ schema, formData, intl }) => {
            let pos = getDefaultSchema(schema, formData, intl);
            getMapVariationSchema(schema, formData, intl, pos);

            return schema;
          },
        },
      ],
    },
  };

  config.blocks.blocksConfig.html.mostUsed = true;

  config.blocks.initialBlocks = {
    ...config.blocks.initialBlocks,
    PointOfInterest: ['title', 'slate', 'multimediaFolderGallery', 'contacts'],
    Event: ['title', 'slate', 'contacts'],
  };

  //remove Volto's blocks and variations
  delete config.blocks.blocksConfig.search; // disabilitato momentaneamente il blocco search 'faccette'
  delete config.blocks.blocksConfig.leadimage; // rimosso perchè non servere per io-visit
  delete config.blocks.blocksConfig.teaser; // TODO: rimosso perchè ancora da implementare per io-visit
  removeListingVariation(config, 'default'); // removes default volto template, because it will be overrided
  removeListingVariation(config, 'imageGallery'); // removes imageGallery template because we have slider template
  removeListingVariation(config, 'summary'); // removes summary template because we don't need it

  // gridBlock keeps its own frozen snapshot of blocksConfig (taken by volto core
  // before addons run), so it never sees the listing variations added/removed above.
  // Re-sync it so variations like "Mappa" are selectable inside the grid block too.
  if (config.blocks.blocksConfig.gridBlock?.blocksConfig) {
    config.blocks.blocksConfig.gridBlock.blocksConfig.listing = cloneDeep(
      config.blocks.blocksConfig.listing,
    );
    // slider variation doesn't make sense nested inside a grid, keep it out
    // of the listing options offered there (still available outside grid)
    config.blocks.blocksConfig.gridBlock.blocksConfig.listing.variations =
      config.blocks.blocksConfig.gridBlock.blocksConfig.listing.variations.filter(
        (variation) => variation.id !== 'slider',
      );
  }

  // customize volto's blocks schema
  config.blocks.blocksConfig.image = {
    ...config.blocks.blocksConfig.image,
    schemaEnhancer: ({ schema, formData, intl }) => {
      addSchemaField(
        schema,
        'image_description',
        intl.formatMessage(messages.image_description_title),
        null,
        {},
        3,
      );
      return schema;
    },
  };
};
